import { uiText } from '../i18n/uiText';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Database,
  HardDrive,
  RefreshCw,
  ServerCog,
  XCircle,
} from 'lucide-react';
import { getApiErrorMessage, systemControlApi } from '../api';
import Badge from '../components/ui/Badge';
import { Link } from 'react-router-dom';
import { formatStatus } from '../utils/formatters';
import PageIntro, { HelpText } from '../components/ui/PageIntro';

const CHECK_ICONS = { database: Database, storage: HardDrive, default: Activity };
const VARIANTS = { ok: 'success', warning: 'warning', error: 'danger' };

function formatGeneratedAt(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' }) + ' МСК';
}

function StatusIcon({ level }) {
  if (level === 'ok') return <CheckCircle2 size={18} />;
  if (level === 'warning') return <AlertTriangle size={18} />;
  return <XCircle size={18} />;
}

export default function SystemControlPage() {
  const [overview, setOverview] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setOverview(null);
    setWorkflow(null);
    try {
      const [overviewResponse, workflowResponse] = await Promise.allSettled([
        systemControlApi.overview(),
        systemControlApi.workflowAudit(),
      ]);
      if (overviewResponse.status === 'fulfilled') setOverview(overviewResponse.value.data);
      if (workflowResponse.status === 'fulfilled') setWorkflow(workflowResponse.value.data);
      setError([overviewResponse, workflowResponse].filter(result => result.status === 'rejected').map(result => getApiErrorMessage(result.reason)).join(' · '));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const checks = useMemo(() => Object.entries(overview?.checks || {}), [overview]);
  const findings = workflow?.findings || [];

  return (
    <div className="workspace-page system-control-page">
      <PageIntro
        eyebrow={uiText("Администрирование")}
        title={uiText("Центр контроля системы")}
        icon={ServerCog}
        description={uiText("Проверки сервисов и ошибки, которые требуют внимания администратора.")}
        aside={(
          <button type="button" className="btn btn-outline" onClick={load} disabled={loading}>
            <RefreshCw size={15} className={loading ? 'spin' : ''} />
            {loading ? uiText("Проверяем…") : uiText("Обновить")}
          </button>
        )}
      >
        <HelpText>{uiText("Экран только читает состояние системы и не пересчитывает аудиты, не меняет статусы и не запускает восстановление.")}</HelpText>
      </PageIntro>

      {error ? <div className="alert alert-error">{error}</div> : null}

      <div className="system-control-summary card">
        <div className="card-body">
          <div>
            <div className="workspace-muted">{uiText("Общее состояние")}</div>
            <div className="system-control-summary-title">
              <StatusIcon level={overview?.status || 'warning'} />
              {!overview ? (loading ? uiText('Проверяем…') : uiText('Нет результатов проверки')) : overview.status === 'ok' ? uiText("Система работает штатно") : overview.status === 'error' ? uiText("Есть критичные проверки") : uiText("Есть пункты, требующие внимания")}
            </div>
          </div>
          <div className="workspace-muted">{uiText("Последняя проверка: ")}{formatGeneratedAt(overview?.generated_at)}</div>
        </div>
      </div>

      <div className="system-control-grid">
        {loading && !overview ? (
          <div className="card"><div className="card-body empty-state">{uiText("Собираем состояние компонентов…")}</div></div>
        ) : checks.map(([key, check]) => {
          const Icon = CHECK_ICONS[key] || CHECK_ICONS.default;
          return (
            <article key={key} className={`card system-control-card is-${check.level}`}>
              <div className="card-body">
                <div className="system-control-card-heading">
                  <span className="system-control-card-icon"><Icon size={18} /></span>
                  <div>
                    <h2>{check.label}</h2>
                    <p>{check.detail}</p>
                  </div>
                  <Badge variant={VARIANTS[check.level] || 'neutral'}>
                    {check.level === 'ok' ? uiText("Норма") : check.level === 'warning' ? uiText("Внимание") : uiText("Ошибка")}
                  </Badge>
                </div>
                {check.next_step && <div className="system-check-help"><strong>{uiText('Что проверить')}</strong><p>{check.next_step}</p></div>}
                {check.counts && <details className="system-check-counts"><summary>{uiText('Состояния записей')}</summary><dl>{Object.entries(check.counts).map(([status, count]) => <React.Fragment key={status}><dt>{({ queued: uiText('В очереди'), sent: uiText('Отправлено'), failed: uiText('Ошибка'), error: uiText('Ошибка'), indexed: uiText('Проиндексировано'), ready: uiText('Готово'), processing: uiText('Обрабатывается'), pending: uiText('Ожидает'), conflict: uiText('Конфликт'), applied: uiText('Применено'), completed: uiText('Завершено') })[status] || status}</dt><dd>{count}</dd></React.Fragment>)}</dl></details>}
              </div>
            </article>
          );
        })}
      </div>

      <section className="card system-control-findings">
        <div className="card-header">
          <div>
            <div className="card-title">{uiText("Проверка статусов и документов аудитов")}</div>
            <p className="workspace-muted">{uiText('Статус в карточке показывает ход работы. Справочный этап рассчитывается по документам; при свободном порядке действий они могут различаться. Проверка ничего не исправляет автоматически.')}</p>
            <div className="workspace-muted">{uiText("Проверено: ")}{workflow?.scanned ?? '—'}{uiText(" · Ошибок: ")}{workflow?.errors ?? '—'}{uiText(" · Предупреждений: ")}{workflow?.warnings ?? '—'}</div>
          </div>
          <Badge variant={!workflow ? 'warning' : workflow.healthy ? (workflow.warnings ? 'warning' : 'success') : 'danger'}>
            {!workflow ? uiText('Нет результатов проверки') : workflow.healthy ? (workflow.warnings ? uiText("Нужна проверка") : uiText("Расхождений нет")) : uiText("Есть ошибки")}
          </Badge>
        </div>
        <div className="card-body" style={{ padding: findings.length ? 0 : undefined }}>
          {!workflow ? (
            <div className="empty-state">{loading ? uiText("Проверяем процессы…") : uiText('Не удалось получить результаты. Повторите проверку.')}</div>
          ) : findings.length === 0 ? (
            <div className="workspace-empty-state">
              <CheckCircle2 size={24} />
              <div className="workspace-empty-state-title">{uiText("Логических конфликтов не найдено")}</div>
              <div className="workspace-empty-state-text">{uiText("Проверка выполнена без изменения текущих аудитов.")}</div>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>{uiText("Уровень")}</th><th>{uiText("Аудит")}</th><th>{uiText("Проверка")}</th><th>{uiText("Что обнаружено")}</th></tr></thead>
                <tbody>
                  {findings.map((item, index) => (
                    <tr key={`${item.audit_id}-${item.code}-${index}`}>
                      <td><Badge variant={item.severity === 'error' ? 'danger' : 'warning'}>{item.severity === 'error' ? uiText("Ошибка") : uiText("Проверить")}</Badge></td>
                      <td><Link to={`/audits/${item.audit_id}`}><strong>#{item.audit_id}</strong> {item.audit_title}</Link></td>
                      <td>{item.check_label || item.code}</td>
                      <td><p>{item.message}</p>{item.stored_stage && <p>{uiText('В карточке')}: <strong>{formatStatus(item.stored_stage)}</strong><br />{uiText('По документам')}: <strong>{formatStatus(item.derived_stage)}</strong></p>}<p className="workspace-muted">{item.next_step}</p></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
